import { useEffect, useMemo, useState } from 'react';
import { Info, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import type { CommitteePaperItem } from '@/hooks/use-committee-papers';
import { useResendEvaluatorMagicLink } from '@/hooks/use-committee-papers';

interface EvaluationLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  paper: CommitteePaperItem | null;
}

const LOCAL_COOLDOWN_MS = 60_000;

function truncateTitle(title: string): string {
  return title.length > 92 ? `${title.slice(0, 89)}...` : title;
}

function getEvaluatorState(
  evaluator: CommitteePaperItem['evaluators'][number],
): 'RESENT' | 'PENDING' | 'SUBMITTED' | 'UNAVAILABLE' {
  if (evaluator.hasCurrentRoundDecision || evaluator.tokenUsedAt) {
    return 'SUBMITTED';
  }

  if (evaluator.canResendEvaluationLink) {
    return 'PENDING';
  }

  return 'UNAVAILABLE';
}

export function EvaluationLinksDialog({ open, onOpenChange, eventId, paper }: EvaluationLinksDialogProps) {
  const [confirmingEvaluatorId, setConfirmingEvaluatorId] = useState<string | null>(null);
  const [recentlyResent, setRecentlyResent] = useState<Record<string, number>>({});
  const resendMutation = useResendEvaluatorMagicLink(eventId);

  useEffect(() => {
    if (!open) {
      setConfirmingEvaluatorId(null);
      setRecentlyResent({});
    }
  }, [open]);

  const evaluators = useMemo(() => paper?.evaluators || [], [paper?.evaluators]);
  const confirmingEvaluator = evaluators.find((item) => item.id === confirmingEvaluatorId) || null;

  const isRecentlyResent = (assignmentId: string) => {
    const sentAt = recentlyResent[assignmentId];
    return Boolean(sentAt && Date.now() - sentAt < LOCAL_COOLDOWN_MS);
  };

  const handleConfirmResend = async () => {
    if (!paper || !confirmingEvaluator) return;

    try {
      await resendMutation.mutateAsync({
        paperId: paper.id,
        assignmentId: confirmingEvaluator.id,
      });

      setRecentlyResent((current) => ({
        ...current,
        [confirmingEvaluator.id]: Date.now(),
      }));
      window.setTimeout(() => {
        setRecentlyResent((current) => {
          const next = { ...current };
          delete next[confirmingEvaluator.id];
          return next;
        });
      }, LOCAL_COOLDOWN_MS);
      toast.success('El enlace de evaluacion fue reenviado correctamente.');
      setConfirmingEvaluatorId(null);
    } catch (error: any) {
      const code = error?.response?.data?.error;
      if (code === 'RESEND_COOLDOWN_ACTIVE') {
        toast.error('RESEND_COOLDOWN_ACTIVE');
      } else if (code === 'EVALUATOR_ALREADY_SUBMITTED') {
        toast.error('El evaluador ya tiene dictamen registrado.');
      } else if (code === 'CLEAN_DOCUMENT_REQUIRED') {
        toast.error('Debe existir una version CLEAN disponible.');
      } else if (code === 'EVALUATION_STATE_NOT_ELIGIBLE' || code === 'ASSIGNMENT_NOT_ELIGIBLE') {
        toast.error('La asignacion no esta disponible para reenvio.');
      } else {
        toast.error('No fue posible reenviar el enlace de evaluacion.');
      }
      setConfirmingEvaluatorId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[86vh] !w-[min(92vw,980px)] !max-w-none flex-col overflow-hidden p-0 sm:!max-w-none">
          <DialogHeader className="shrink-0 px-8 pt-8">
            <DialogTitle className="text-3xl font-bold text-[#0f2744]">Enlaces de evaluacion</DialogTitle>
            {paper ? (
              <p className="text-sm text-slate-600">
                {paper.folio} - {truncateTitle(paper.title)}
              </p>
            ) : null}
          </DialogHeader>

          <div className="flex-1 space-y-5 overflow-y-auto px-8 py-6">
            <div className="flex gap-4 rounded-lg border border-blue-200 bg-blue-50 p-5 text-[#0f2744]">
              <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-400 text-blue-600">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Reenvio de enlaces a evaluadores pendientes</h3>
                <p className="mt-1 text-sm leading-6 text-slate-700">
                  Esta accion permite reenviar el enlace de evaluacion a los evaluadores que aun no han emitido su
                  dictamen. No modifica la asignacion de evaluadores ni altera dictamenes ya registrados.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-lg font-semibold text-[#0f2744]">Evaluadores asignados</h3>
              <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                <div className="grid grid-cols-[minmax(260px,1fr)_170px_220px] gap-4 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                  <span>Evaluador</span>
                  <span>Estado</span>
                  <span>Accion</span>
                </div>
                <div className="divide-y divide-slate-200">
                  {evaluators.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-slate-500">No hay evaluadores asignados.</div>
                  ) : (
                    evaluators.map((evaluator) => {
                      const recent = isRecentlyResent(evaluator.id);
                      const state = recent ? 'RESENT' : getEvaluatorState(evaluator);
                      const canResend = state === 'PENDING' && !resendMutation.isPending;

                      return (
                        <div
                          key={evaluator.id}
                          className="grid grid-cols-[minmax(260px,1fr)_170px_220px] items-center gap-4 px-4 py-3 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800">{evaluator.name}</p>
                            <p className="mt-1 truncate text-xs text-slate-500">{evaluator.email}</p>
                          </div>
                          <span>
                            {state === 'RESENT' ? (
                              <Badge className="border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-50">
                                Reenviado recientemente
                              </Badge>
                            ) : state === 'PENDING' ? (
                              <Badge className="border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-50">
                                Pendiente
                              </Badge>
                            ) : state === 'SUBMITTED' ? (
                              <Badge className="border border-green-300 bg-green-50 text-green-700 hover:bg-green-50">
                                Dictamen recibido
                              </Badge>
                            ) : (
                              <Badge className="border border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-50">
                                Sin accion
                              </Badge>
                            )}
                          </span>
                          <span>
                            {state === 'PENDING' || state === 'RESENT' ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-[#1e3a5f] text-[#1e3a5f]"
                                disabled={!canResend}
                                onClick={() => setConfirmingEvaluatorId(evaluator.id)}
                              >
                                {resendMutation.isPending && confirmingEvaluatorId === evaluator.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="mr-2 h-4 w-4" />
                                )}
                                Reenviar enlace
                              </Button>
                            ) : (
                              <span className="text-sm text-slate-500">Dictamen registrado</span>
                            )}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-500">
                Nota: El sistema solo permite reenviar enlaces a evaluadores pendientes y vigentes en la ronda actual.
              </p>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-slate-200 px-8 py-5">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(confirmingEvaluator)} onOpenChange={(open) => !open && setConfirmingEvaluatorId(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full border border-blue-400 text-blue-600">
              <Send className="h-5 w-5" />
            </div>
            <AlertDialogTitle>Confirmar reenvio de enlace</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 text-slate-600">
              <span className="block">Se reenviara el enlace de evaluacion al evaluador seleccionado.</span>
              <span className="block">
                Esta accion no modifica la asignacion de evaluadores ni altera dictamenes registrados.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resendMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={resendMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmResend();
              }}
            >
              {resendMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Reenviar enlace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
